/**
 * Extract tabular blocks from AI answers for Excel-friendly copy.
 * Supports markdown pipe tables and tab-separated (TSV) blocks.
 */

function stripFence(text) {
  const trimmed = text.trim()
  const match = trimmed.match(/^```[\w-]*\s*\n([\s\S]*?)\n?```$/i)
  return match ? match[1].trimEnd() : trimmed
}

function isSeparatorRow(cells) {
  if (!cells.length) return false
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell.trim()) || cell.trim() === '')
}

function splitPipeRow(line) {
  let text = line.trim()
  if (text.startsWith('|')) text = text.slice(1)
  if (text.endsWith('|')) text = text.slice(0, -1)
  return text.split('|').map((cell) => cell.trim())
}

function parsePipeTable(lines) {
  const rows = []
  for (const line of lines) {
    if (!line.includes('|')) continue
    const cells = splitPipeRow(line)
    if (!cells.length || isSeparatorRow(cells)) continue
    rows.push(cells)
  }
  if (rows.length < 2) return null
  const width = Math.max(...rows.map((r) => r.length))
  if (width < 2) return null
  return rows.map((row) => {
    const padded = [...row]
    while (padded.length < width) padded.push('')
    return padded
  })
}

function parseTsvTable(lines) {
  const rows = lines
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line.trim().length > 0)
    .map((line) => line.split('\t').map((cell) => cell.trim()))

  if (rows.length < 2) return null
  const withTabs = lines.some((line) => line.includes('\t'))
  if (!withTabs) return null
  const width = Math.max(...rows.map((r) => r.length))
  if (width < 2) return null
  return rows.map((row) => {
    const padded = [...row]
    while (padded.length < width) padded.push('')
    return padded
  })
}

function parseTableText(raw) {
  const body = stripFence(raw)
  const lines = body.split(/\n/)
  return parsePipeTable(lines) || parseTsvTable(lines)
}

/**
 * Split an AI answer into prose and table segments.
 * @returns {{ type: 'prose' | 'table', text?: string, rows?: string[][] }[]}
 */
export function parseAiAnswerSegments(answer) {
  const text = answer || ''
  if (!text.trim()) return []

  const segments = []
  const fenceRe = /```[\w-]*\s*\n([\s\S]*?)```/g
  let lastIndex = 0
  let match

  while ((match = fenceRe.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'prose', text: text.slice(lastIndex, match.index) })
    }
    const block = match[0]
    const rows = parseTableText(block)
    if (rows) {
      segments.push({ type: 'table', rows, source: 'fence' })
    } else {
      segments.push({ type: 'prose', text: block })
    }
    lastIndex = match.index + block.length
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'prose', text: text.slice(lastIndex) })
  }

  // Promote unfenced TSV/pipe runs inside prose into tables.
  const promoted = []
  for (const segment of segments) {
    if (segment.type !== 'prose') {
      promoted.push(segment)
      continue
    }
    promoted.push(...promoteTablesInProse(segment.text))
  }

  return promoted.filter((segment) => {
    if (segment.type === 'prose') return (segment.text || '').trim().length > 0
    return segment.rows && segment.rows.length > 0
  })
}

function promoteTablesInProse(prose) {
  const lines = (prose || '').split(/\n/)
  const out = []
  let buffer = []
  let mode = null // 'pipe' | 'tsv' | null

  function flushBuffer() {
    if (!buffer.length) return
    const rows = mode === 'pipe' ? parsePipeTable(buffer) : parseTsvTable(buffer)
    if (rows) {
      out.push({ type: 'table', rows, source: mode })
    } else {
      out.push({ type: 'prose', text: `${buffer.join('\n')}\n` })
    }
    buffer = []
    mode = null
  }

  function flushProse(chunk) {
    if (!chunk) return
    out.push({ type: 'prose', text: chunk })
  }

  let proseBuf = []

  for (const line of lines) {
    const isPipe = line.includes('|') && splitPipeRow(line).length >= 2
    const isTsv = line.includes('\t') && line.split('\t').length >= 2

    const lineMode = isPipe ? 'pipe' : isTsv ? 'tsv' : null
    if (lineMode) {
      if (proseBuf.length) {
        flushProse(`${proseBuf.join('\n')}\n`)
        proseBuf = []
      }
      if (mode && mode !== lineMode) flushBuffer()
      mode = lineMode
      buffer.push(line)
      continue
    }

    if (mode) {
      // Allow blank line inside a table only if we already have a header + row.
      if (!line.trim() && buffer.length >= 2) {
        flushBuffer()
        continue
      }
      flushBuffer()
    }
    proseBuf.push(line)
  }

  if (mode) flushBuffer()
  if (proseBuf.length) flushProse(proseBuf.join('\n'))

  return out
}

/** Windows Excel prefers CRLF; always use real tab separators. */
export function tableRowsToExcelTsv(rows) {
  return (rows || [])
    .map((row) =>
      row
        .map((cell) => String(cell ?? '').replace(/\t/g, ' ').replace(/\r?\n/g, ' '))
        .join('\t'),
    )
    .join('\r\n')
}

export async function copyTableForExcel(rows) {
  const payload = tableRowsToExcelTsv(rows)
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(payload)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = payload
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}
